import type { Envelope } from '../domain/schemas';

/**
 * Reaction envelopes (Phase 4A): pure functions of the time since a trigger, returning a
 * level in [0, 1]. Nothing is accumulated between frames, so the level at any instant is
 * the same whether playback reached it continuously or by seeking.
 *
 *  - instant: jumps to 1 on the trigger, holds, then recovers over `decay`;
 *  - smooth: eased (cosine) rise over `attack`, hold, eased fall over `decay`;
 *  - impulse: a very short spike (attack 0, no hold, decay ≤ 0.12 s);
 *  - hold: linear rise over `attack`, sustained for `hold`, then released over `decay`.
 * `easedRecovery` makes the fall quadratic (fast first, then settling) instead of linear.
 */
export const IMPULSE_MAX_DECAY = 0.12;

export interface ShapedEnvelope {
  attack: number;
  hold: number;
  decay: number;
  rise: (u: number) => number;
  fall: (u: number) => number;
}

const linear = (u: number) => u;
const cosine = (u: number) => 0.5 - 0.5 * Math.cos(Math.PI * u);

export function shapeOf(e: Envelope): ShapedEnvelope {
  const fall = e.easedRecovery ? (u: number) => 1 - (1 - u) * (1 - u) : linear;
  switch (e.shape) {
    case 'instant':
      return { attack: 0, hold: e.hold, decay: e.decay, rise: linear, fall };
    case 'smooth':
      return { attack: e.attack, hold: e.hold, decay: e.decay, rise: cosine, fall: e.easedRecovery ? fall : cosine };
    case 'impulse':
      return { attack: 0, hold: 0, decay: Math.min(e.decay > 0 ? e.decay : IMPULSE_MAX_DECAY, IMPULSE_MAX_DECAY), rise: linear, fall };
    case 'hold':
      return { attack: e.attack, hold: e.hold, decay: e.decay, rise: linear, fall };
  }
}

/** Total seconds an envelope is non-zero after its trigger. */
export const envelopeLength = (e: Envelope): number => {
  const s = shapeOf(e);
  return s.attack + s.hold + s.decay;
};

/** Level of an envelope `t` seconds after its trigger (0 before the trigger and after the end). */
export function envelopeAt(e: Envelope | ShapedEnvelope, t: number): number {
  const s = 'rise' in e ? e : shapeOf(e);
  if (!(t >= 0)) return 0;
  if (t < s.attack) return s.rise(t / s.attack);
  const afterAttack = t - s.attack;
  if (afterAttack <= s.hold) return 1;
  const intoDecay = afterAttack - s.hold;
  if (s.decay <= 0 || intoDecay >= s.decay) return 0;
  return 1 - s.fall(intoDecay / s.decay);
}

/** Envelope presets used by the editor and prompts. */
export const ENVELOPE_PRESETS: Record<Envelope['shape'], Envelope> = {
  instant: { shape: 'instant', attack: 0, hold: 0, decay: 0.25, easedRecovery: true },
  smooth: { shape: 'smooth', attack: 0.06, hold: 0, decay: 0.3, easedRecovery: false },
  impulse: { shape: 'impulse', attack: 0, hold: 0, decay: 0.1, easedRecovery: false },
  hold: { shape: 'hold', attack: 0.02, hold: 0.4, decay: 0.2, easedRecovery: true },
};
